import React from "react";
import { AdminImportCard } from "Main/Admin/AdminImportCard";
import { AdminImportStyle } from "Main/Admin/AdminImportStyle";
import { normalizeHeader, parseDateOnly, parseRows } from "Main/Admin/AdminImportCsvUtils";
import { CsvImportMessageTexts, CsvImportStatusTexts, defaultCsvImportStatusTexts, getRequestStatusTitle, getValidationStatusTitle, RequestStatus, sortImportedRowsByDate, toLocalDate, useCsvImportState } from "Main/Admin/AdminImportShared";
import { Icon } from "Shared/Components/Icon/Style";
import { useApi } from "Shared/Context/API";
import useLang from "Shared/Context/LangContext";

export interface PodImportRow {
    date: string;
    podId: string;
    reservationName: string;
    capacity: number;
    capacityText: string;
    isValid: boolean;
    invalidReason?: string;
    sortDate?: string;
    requestStatus: RequestStatus;
    requestMessage?: string;
}

const getPodInvalidReason = (
    parsedDate: string | undefined,
    podId: string,
    reservationName: string,
    hasValidCapacity: boolean,
    texts: {
        invalidDate: string;
        podIdMissing: string;
        reservationNameMissing: string;
        invalidCapacity: string;
    },
) => {
    if (!parsedDate) return texts.invalidDate;
    if (podId.length === 0) return texts.podIdMissing;
    if (reservationName.length === 0) return texts.reservationNameMissing;
    if (!hasValidCapacity) return texts.invalidCapacity;
    return undefined;
};

const hasPodImportHeader = (rawDate: string, rawPodId: string, rawReservationName: string, rawCapacity: string) => {
    const first = normalizeHeader(rawDate);
    const second = normalizeHeader(rawPodId);
    const third = normalizeHeader(rawReservationName);
    const fourth = normalizeHeader(rawCapacity);

    return first === "date"
        && (second === "podid" || second === "pod")
        && (third === "reservationname" || third === "name" || third === "text")
        && (fourth === "capacity" || fourth === "count");
};

const parseCapacity = (rawCapacity: string) => {
    const parsedCapacity = Number.parseInt(rawCapacity, 10);
    const isValid = Number.isInteger(parsedCapacity) && parsedCapacity > 0 && parsedCapacity.toString() === rawCapacity;

    return {
        parsedCapacity,
        hasValidCapacity: isValid,
    };
};

export const PodCsvImport = () => {
    const api = useApi();
    const { curLangData } = useLang();
    const [showAllRows, setShowAllRows] = React.useState(false);
    const {
        rows,
        setRows,
        fileName,
        message,
        error,
        setMessage,
        setError,
        isBatchRunning,
        hasBatchExecuted,
        validRowCount,
        resetForNewFile,
        executeBatch,
    } = useCsvImportState<PodImportRow>();

    const statusTexts = React.useMemo<CsvImportStatusTexts>(() => ({
        invalidRow: curLangData?.text.CsvImportStatusInvalidRow ?? defaultCsvImportStatusTexts.invalidRow,
        validRow: curLangData?.text.CsvImportStatusValidRow ?? defaultCsvImportStatusTexts.validRow,
        notExecutedBecauseValidationFailed: curLangData?.text.CsvImportStatusNotExecutedBecauseValidationFailed ?? defaultCsvImportStatusTexts.notExecutedBecauseValidationFailed,
        uploadedSuccessfully: curLangData?.text.CsvImportStatusUploadedSuccessfully ?? defaultCsvImportStatusTexts.uploadedSuccessfully,
        uploadFailed: curLangData?.text.CsvImportStatusUploadFailed ?? defaultCsvImportStatusTexts.uploadFailed,
        notExecutedYet: curLangData?.text.CsvImportStatusNotExecutedYet ?? defaultCsvImportStatusTexts.notExecutedYet,
    }), [curLangData?.text.CsvImportStatusInvalidRow, curLangData?.text.CsvImportStatusNotExecutedBecauseValidationFailed, curLangData?.text.CsvImportStatusNotExecutedYet, curLangData?.text.CsvImportStatusUploadFailed, curLangData?.text.CsvImportStatusUploadedSuccessfully, curLangData?.text.CsvImportStatusValidRow]);

    const messageTexts = React.useMemo<CsvImportMessageTexts>(() => ({
        noValidRows: curLangData?.error.CsvImportNoValidRows ?? "No valid rows available for upload.",
        uploadedSuccessfully: curLangData?.text.CsvImportStatusUploadedSuccessfully ?? "Uploaded successfully.",
        requestFailed: curLangData?.error.CsvImportRequestFailed ?? "Request failed.",
        formatStartUpload: (count, itemLabel) => {
            const template = curLangData?.info.CsvImportStartUpload ?? "Starting upload for {count} {itemLabel} row(s)...";
            return template
                .replace("{count}", count.toString())
                .replace("{itemLabel}", itemLabel);
        },
        formatFinishedUpload: (itemLabel, successCount, failedCount) => {
            const template = curLangData?.info.CsvImportFinishedUpload ?? "{itemLabel} upload finished: {success} succeeded, {failed} failed.";
            const defaultItemLabel = `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)}`;
            return template
                .replace("{itemLabel}", defaultItemLabel)
                .replace("{success}", successCount.toString())
                .replace("{failed}", failedCount.toString());
        },
    }), [curLangData?.error.CsvImportNoValidRows, curLangData?.error.CsvImportRequestFailed, curLangData?.info.CsvImportFinishedUpload, curLangData?.info.CsvImportStartUpload, curLangData?.text.CsvImportStatusUploadedSuccessfully]);

    const onFileImport = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        resetForNewFile(file.name);

        const content = await file.text();
        const rows = parseRows(content);
        const importedRows: PodImportRow[] = [];
        let invalidRows = 0;
        const seenBookings = new Set<string>();

        rows.forEach((row, rowIndex) => {
            const rawDate = (row.values[0] ?? "").trim();
            const rawPodId = (row.values[1] ?? "").trim();
            const rawReservationName = (row.values[2] ?? "").trim();
            const rawCapacity = (row.values[3] ?? "").trim();

            if (rowIndex === 0 && hasPodImportHeader(rawDate, rawPodId, rawReservationName, rawCapacity)) {
                return;
            }

            const parsedDate = parseDateOnly(rawDate);
            const { parsedCapacity, hasValidCapacity } = parseCapacity(rawCapacity);
            let invalidReason = getPodInvalidReason(
                parsedDate,
                rawPodId,
                rawReservationName,
                hasValidCapacity,
                {
                    invalidDate: curLangData?.error.CsvImportInvalidDateFormat ?? "Invalid date format.",
                    podIdMissing: curLangData?.error.CsvImportPodIdMissing ?? "PodId is missing.",
                    reservationNameMissing: curLangData?.error.CsvImportReservationNameMissing ?? "Reservation name is missing.",
                    invalidCapacity: curLangData?.error.CsvImportCapacityPositiveInteger ?? "Capacity must be a positive integer.",
                },
            );
            let isValid = !invalidReason;

            if (isValid && parsedDate) {
                const duplicateKey = `${parsedDate}|${rawPodId.toLowerCase()}`;
                if (seenBookings.has(duplicateKey)) {
                    isValid = false;
                    invalidReason = curLangData?.error.CsvImportPodDuplicateEntry ?? "Duplicated Entry for this Pod on this date.";
                } else {
                    seenBookings.add(duplicateKey);
                }
            }

            if (!isValid) {
                invalidRows++;
            }

            importedRows.push({
                date: parsedDate ?? rawDate,
                podId: rawPodId,
                reservationName: rawReservationName,
                capacity: hasValidCapacity ? parsedCapacity : Number.NaN,
                capacityText: hasValidCapacity ? parsedCapacity.toString() : rawCapacity,
                isValid,
                invalidReason,
                sortDate: parsedDate,
                requestStatus: "idle",
                requestMessage: undefined,
            });
        });

        setRows(sortImportedRowsByDate(importedRows));

        if (importedRows.length === 0) {
            setError(curLangData?.error.CsvImportPodNoRowsFound ?? "No rows found. Expected columns: date, podId, reservation name, capacity.");
        } else {
            const validRows = importedRows.length - invalidRows;
            const template = curLangData?.info.CsvImportPodLoadedRows ?? "Loaded {total} pod row(s): {valid} valid, {invalid} invalid.";
            setMessage(template
                .replace("{total}", importedRows.length.toString())
                .replace("{valid}", validRows.toString())
                .replace("{invalid}", invalidRows.toString()));
        }

        event.target.value = "";
    }, [curLangData?.error.CsvImportCapacityPositiveInteger, curLangData?.error.CsvImportInvalidDateFormat, curLangData?.error.CsvImportPodDuplicateEntry, curLangData?.error.CsvImportPodIdMissing, curLangData?.error.CsvImportPodNoRowsFound, curLangData?.error.CsvImportReservationNameMissing, curLangData?.info.CsvImportPodLoadedRows, resetForNewFile, setError, setMessage, setRows]);

    const onBatchExecute = React.useCallback(async () => {
        await executeBatch(
            "pod",
            async (row) => {
                await api.Admin.addPodReservation({
                    day: toLocalDate(row.sortDate),
                    podId: row.podId,
                    text: row.reservationName,
                    capacity: row.capacity,
                });
            },
            messageTexts,
        );
    }, [api.Admin, executeBatch, messageTexts]);

    const visibleRows = React.useMemo(
        () => showAllRows ? rows : rows.filter((row) => !row.isValid || row.requestStatus === "error"),
        [rows, showAllRows]
    );

    return (
        <AdminImportCard<PodImportRow>
            title={curLangData?.text.CsvImportPodTitle ?? "Pod CSV Import"}
            description={curLangData?.text.CsvImportPodDescription ?? "Expected columns: Date (dateonly), PodId, Reservation name, Capacity (positive integer)."}
            uploadButtonText={curLangData?.text.CsvImportPodUploadButton ?? "Upload Pod CSV"}
            fileName={fileName}
            message={message}
            error={error}
            emptyText={showAllRows
                ? (curLangData?.text.CsvImportPodEmptyAll ?? "No pod CSV data loaded yet.")
                : (curLangData?.text.CsvImportPodEmptyInvalid ?? "No invalid or failed pod rows to show.")}
            headers={[
                curLangData?.text.CsvImportHeaderValidation ?? "Validation",
                curLangData?.text.CsvImportHeaderRequest ?? "Request",
                curLangData?.text.CsvImportHeaderDate ?? "Date",
                curLangData?.text.CsvImportHeaderPodId ?? "PodId",
                curLangData?.text.CsvImportHeaderReservationName ?? "Reservation name",
                curLangData?.text.CsvImportHeaderCapacity ?? "Capacity",
            ]}
            rows={visibleRows}
            rowKey={(row, index) => `${row.date}-${row.podId}-${row.reservationName}-${row.capacityText}-${row.isValid}-${row.requestStatus}-${index}`}
            renderRowCells={(row) => (
                <>
                    <AdminImportStyle.ValidityCell isValid={row.isValid && row.requestStatus !== "error"}>
                        <AdminImportStyle.ValidityIconWrap title={getValidationStatusTitle(row, statusTexts)}>
                            <Icon.PfeilRechts isBold size={"20px"} />
                        </AdminImportStyle.ValidityIconWrap>
                    </AdminImportStyle.ValidityCell>
                    <AdminImportStyle.RequestStatusCell status={row.requestStatus}>
                        <AdminImportStyle.ValidityIconWrap title={getRequestStatusTitle(row, statusTexts)}>
                            {row.requestStatus === "success" && <Icon.Haken isBold size={"20px"} />}
                            {row.requestStatus === "error" && <Icon.X isBold size={"20px"} />}
                            {row.requestStatus === "idle" && <Icon.PfeilRechts isBold size={"20px"} />}
                        </AdminImportStyle.ValidityIconWrap>
                    </AdminImportStyle.RequestStatusCell>
                    <AdminImportStyle.TableCell>{row.date}</AdminImportStyle.TableCell>
                    <AdminImportStyle.TableCell>{row.podId}</AdminImportStyle.TableCell>
                    <AdminImportStyle.TableCell>{row.reservationName}</AdminImportStyle.TableCell>
                    <AdminImportStyle.TableCell>{row.capacityText}</AdminImportStyle.TableCell>
                </>
            )}
            onFileImport={onFileImport}
            batchButtonText={curLangData?.text.CsvImportPodExecuteUpload ?? "Execute Pod Upload"}
            uploadingText={curLangData?.text.CsvImportUploading ?? "Uploading..."}
            onBatchExecute={onBatchExecute}
            isBatchRunning={isBatchRunning}
            isBatchDisabled={hasBatchExecuted || validRowCount === 0}
            controls={(
                <AdminImportStyle.ControlsRow>
                    <AdminImportStyle.CheckboxLabel>
                        <AdminImportStyle.CheckboxInput
                            type={"checkbox"}
                            checked={showAllRows}
                            onChange={(event) => setShowAllRows(event.target.checked)}
                        />
                        {curLangData?.text.CsvImportShowAllRows ?? "Show all rows"}
                    </AdminImportStyle.CheckboxLabel>
                </AdminImportStyle.ControlsRow>
            )}
        />
    );
};
